import { useExperienceApi } from '@/shared/api/useExperienceApi.ts';
import { useQuery } from '@tanstack/vue-query';
import { getBootstrapData } from '@experience/core-hostapp-share-lib';
import { useDisplayCurrencyQueryParams } from '@/shared/composables/useDisplayCurrencyQueryParams.ts';
import type { GetExperiencesRecommendationsData } from '@/shared/api/experience';
import { LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID } from '@/entities/experience/recommendation/constants';

export const useGetExperiencesRecommendations = (
  params: Ref<GetExperiencesRecommendationsData['query']>,
  enabled: MaybeRef<boolean> = false,
) => {
  const { getExperiencesRecommendations } = useExperienceApi();
  const displayCurrencyQueryParams = useDisplayCurrencyQueryParams();

  const excludeNeedsLicense = computed(() => {
    return getBootstrapData().hideUnlicensedExperiencesForPartner;
  });

  const recommendationsQueryKeyParams = computed(() => ({
    ...params.value,
    ...displayCurrencyQueryParams.value,
  }));

  const {
    data: experiencesData,
    isFetching,
    isFetched,
  } = useQuery({
    queryKey: computed(() => ['getExperiencesRecommendations', recommendationsQueryKeyParams.value]),
    queryFn: async () => {
      const response = await getExperiencesRecommendations({
        query: {
          ...recommendationsQueryKeyParams.value,
          limit: 0,
          offset: 0,
          extended_movement_types: true,
          exclude_needs_license: excludeNeedsLicense.value,
          photos_count: 1,
        },
      });

      return response.data;
    },
    enabled,
    placeholderData: (prevData, previousQuery) => {
      const prevParams = previousQuery?.queryKey?.[1] as { display_currency?: string } | undefined;
      const nextCurrency = recommendationsQueryKeyParams.value.display_currency;

      if (prevParams?.display_currency !== nextCurrency) {
        return undefined;
      }

      return (
        prevData ?? {
          count: 0,
          next: null,
          previous: null,
          results: [],
          recommendation_algorithm_id: LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID,
          recommendation_algorithm_name: null,
          recommendation_algorithm_type: null,
        }
      );
    },
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Метаданные алгоритма рекомендаций используются для SOGU-событий листинга.
  const recommendationAlgorithmMeta = computed(() => {
    const data = experiencesData.value;

    return {
      recommendation_algorithm_id:
        data?.recommendation_algorithm_id?.trim() || LISTING_RECOMMENDATIONS_ALGORITHM_FALLBACK_ID,
      recommendation_algorithm_name: data?.recommendation_algorithm_name ?? undefined,
      recommendation_algorithm_type: data?.recommendation_algorithm_type ?? undefined,
    };
  });

  return {
    experiencesData,
    isFetching,
    isFetched,
    recommendationAlgorithmMeta,
  };
};
